/**
 * Quiz API — narrow client over POST /v1/quiz/answer.
 *
 * Phase 1C.1 endpoint (deployed; verified by 8 vitest unit tests):
 *  - POST /v1/quiz/answer
 *      UPSERT quiz_answers (UNIQUE device+child+question_key)
 *      and opportunistically update user_preferences.
 *      Returns { ok, answer, preferences }.
 *
 * Validation contract (zod in apps/api/src/lib/validators.ts):
 *  - childProfileId: int positive
 *  - questionKey:    enum('goals'|'tema'|'activity'|'difficulty'|'frequency')
 *  - questionOrder:  literal 1|2|3|4|5
 *  - answerPayload:  { selected: string[]; skipped: bool;
 *                      timeSpentMs: int 0-600000; customText?: string <=500 }
 *
 * Quiz answers are append-only audit (UPSERT on (device, child, question_key)
 * — re-onboarding overwrites the previous answer for the same question).
 * user_preferences is the denormalized current state.
 */

import { apiFetch } from './client';
import type {
  QuizAnswer,
  QuizAnswerInsert,
  QuizAnswerPayload,
  QuizQuestionKey,
  QuizQuestionOrder,
  UserPreferences,
} from '@funpaper/shared';

export type SubmitQuizAnswerInput = {
  childProfileId: number;
  questionKey: QuizQuestionKey;
  questionOrder: QuizQuestionOrder;
  answerPayload: QuizAnswerPayload;
};

type SubmitQuizAnswerResponse = {
  ok: true;
  answer: QuizAnswer;
  preferences: UserPreferences;
};

export async function submitQuizAnswer(
  input: SubmitQuizAnswerInput,
): Promise<{ answer: QuizAnswer; preferences: UserPreferences }> {
  const body: QuizAnswerInsert = {
    childProfileId: input.childProfileId,
    questionKey: input.questionKey,
    questionOrder: input.questionOrder,
    answerPayload: input.answerPayload,
  };
  const response = await apiFetch<SubmitQuizAnswerResponse>('/v1/quiz/answer', {
    method: 'POST',
    body,
  });
  return { answer: response.answer, preferences: response.preferences };
}
